<template>
  <jz-container flex column overflow-y-auto>
    <layout-full-card
      :back-fallback="backRoute"
      id="assignment-detail-view"
      :breadcrumbs="breadcrumbs"
      :loading="!assignment"
    >
      <jz-container v-if="assignment" id="assignment-details">
        <div class="header">
          <jz-title>
            <jz-icon :src="bookIcon" />

            {{ assignment.title }}
          </jz-title>
        </div>

        <jz-container class="group-name">
          <jz-container class="title">{{ "assignment.detail.group" | __ }}</jz-container>
          <jz-container class="content">{{ assignment.group.name }}</jz-container>
        </jz-container>

        <jz-container class="assignment-status">
          <div :class="['assignment-dates', statusClass]">
            <div class="title">{{ "assignment.detail.submission-dates" | __ }}</div>

            <div class="content">{{ validPeriod }}</div>
          </div>

          <div v-if="submittedDate" :class="['submission-date', statusClass]">
            <div class="title">{{ "assignment.detail.submitted-date" | __ }}</div>

            <div class="content">{{ submittedDate }}</div>
          </div>

          <div class="submission-status">
            <jz-container :class="['label', statusClass]">
              <img :src="statusIcon" />

              {{ statusLabel }}
            </jz-container>
          </div>
        </jz-container>

        <jz-container class="notes">
          <p v-for="(paragraph, index) in assignment.notes" :key="index" class="note">
            {{ paragraph }}
          </p>
        </jz-container>
      </jz-container>

      <jz-container v-if="assignment" id="assignment-exercises">
        <exercise-list-item
          v-for="(exercise, index) in assignment.exercises"
          :key="index"
          :completion-icon="completionIcon(exercise)"
          :exercise="exercise"
          @click.native="goToExerciseFromExerciseEntry(exercise, assignmentId)"
        />
      </jz-container>
    </layout-full-card>

    <feedback-popup v-if="showFeedbackForm" :logId="feedbackLogId" @close="handleFeedbackClose" />
  </jz-container>
</template>

<script>
import { ROUTES, VUEX_ASSIGNMENT_ACTION_NAMES, VUEX_ASSIGNMENT_NAMESPACE } from "@tt-app/constants";
import { mapState } from "vuex";

import ExerciseListItem from "@tt-app/components/exercise/ExerciseListItem.vue";
import LayoutFullCard from "@tt-app/layouts/LayoutFullCard.vue";
import FeedbackPopup from "../../components/feedback/FeedbackPopup";

import alertIcon from "@joyz/app-core/assets/icons/white/Alert.svg";
import checkCircleIcon from "@joyz/app-core/assets/icons/white/CheckCircle.svg";
import correctIcon from "@joyz/app-core/assets/icons/Correct.svg";
import hourglassIcon from "@joyz/app-core/assets/icons/white/Hourglass.svg";
import rocketIcon from "@joyz/app-core/assets/icons/white/Rocket.svg";
import backIcon from "@joyz/app-core/assets/icons/black/Back.svg";
import bookIcon from "@joyz/app-core/assets/icons/default/Book.svg";
import microphoneIcon from "@joyz/app-core/assets/icons/default/Microphone.svg";
import GoToExerciseMixin from "@tt-app/components/exercise/mixins/GoToExerciseMixin";

const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;

export default {
  name: "AssignmentDetail",
  components: {
    FeedbackPopup,
    ExerciseListItem,
    LayoutFullCard
  },
  mixins: [GoToExerciseMixin],
  data() {
    return {
      feedbackParams: undefined,
      from: undefined,
      assigmentId: undefined
    };
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.feedbackParams = to.params.feedbackParams;

      if (!from.path.match(/exercise/)) {
        vm.$store.commit("assignmentDetailsEntryRoute", from);
      }
    });
  },
  beforeRouteLeave(to, from, next) {
    if (!to.path.match(/exercise/)) {
      this.$store.commit("assignmentDetailsEntryRoute", undefined);
    }
    next();
  },
  computed: {
    ...mapState({
      assignment: state => state.assignment.details,
      assignmentDetailsEntryRoute: state => state.navigation.assignmentDetails.entryRoute
    }),
    backRoute() {
      if (this.assignmentDetailsEntryRoute) {
        return this.assignmentDetailsEntryRoute;
      }

      return {
        name: ROUTES.HOME.NAME
      };
    },
    breadcrumbs() {
      const title = (this.assignment && this.assignment.title) || "";
      if (
        this.assignmentDetailsEntryRoute &&
        this.assignmentDetailsEntryRoute.path.match(/assignment\/explore/)
      ) {
        return [this.$__("nav.home"), this.$__("assignments.explore.title"), title];
      } else {
        return [this.$__("nav.home"), title];
      }
    },
    feedbackLogId() {
      if (!this.feedbackParams) return;

      return this.feedbackParams.logId;
    },
    previousPath() {
      return this.assignmentDetailsEntryRoute && this.assignmentDetailsEntryRoute.path;
    },
    showFeedbackForm() {
      if (!this.feedbackParams) return false;

      return this.feedbackParams.withFeedback === "true";
    },
    statusClass() {
      const classes = {
        1: "before-start-date",
        2: "after-start-before-due-not-submitted",
        3: "after-start-before-due-submitted",
        4: "after-due-not-submitted",
        5: "after-due-submitted-before-due",
        6: "after-due-submitted-after-due"
      };
      return classes[this.assignment.status];
    },
    statusIcon() {
      const icons = {
        1: rocketIcon,
        2: hourglassIcon,
        3: checkCircleIcon,
        4: alertIcon,
        5: checkCircleIcon,
        6: checkCircleIcon
      };
      return icons[this.assignment.status];
    },
    statusLabel() {
      return this.$__(`assignment.status.${this.assignment.status}`);
    },
    submittedDate() {
      let { submitted } = this.assignment;
      if (!submitted) return;

      submitted = new Date(Date.parse(submitted));

      const submittedYear = submitted.getFullYear();
      const submittedMonth = ("0" + (submitted.getMonth() + 1)).slice(-2);
      const submittedDay = ("0" + submitted.getDate()).slice(-2);

      return `${submittedYear}/${submittedMonth}/${submittedDay}`;
    },
    validPeriod() {
      let { startDate, dueDate } = this.assignment;
      startDate = new Date(Date.parse(startDate));
      dueDate = new Date(Date.parse(dueDate));

      const startYear = startDate.getFullYear();
      const startMonth = ("0" + (startDate.getMonth() + 1)).slice(-2);
      const startDay = ("0" + startDate.getDate()).slice(-2);

      const dueYear = dueDate.getFullYear();
      const dueMonth = ("0" + (dueDate.getMonth() + 1)).slice(-2);
      const dueDay = ("0" + dueDate.getDate()).slice(-2);

      return `${startYear}/${startMonth}/${startDay} ~ ${dueYear}/${dueMonth}/${dueDay}`;
    }
  },
  created() {
    this.assignmentId = this.$router.currentRoute.params.assignmentId;
    this.backIcon = backIcon;
    this.bookIcon = bookIcon;
    this.icons = {
      1: microphoneIcon
    };

    if (this.assignmentId) {
      this.fetchAssignmentDetails();
    }
  },
  methods: {
    completionIcon(exercise) {
      if (exercise.finished) {
        return correctIcon;
      }
      return "";
    },
    async fetchAssignmentDetails() {
      await this.$store.dispatch(`${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`, {
        assignmentId: this.assignmentId
      });
    },

    handleFeedbackClose() {
      this.feedbackParams.withFeedback = false;
    }
  }
};
</script>

<style lang="scss">
#assignment-detail-view.layout-full-card {
  padding: $gutter ($gutter / 2) ($gutter / 2) ($gutter / 2);
  height: 100%;

  .jz-card.content {
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: min-content auto minmax(0, min-content);
    max-width: 920px;

    #assignment-details {
      .group-name {
        padding: $gutter;
      }

      .assignment-status {
        display: grid;
        gap: $gutter;
        grid-template-rows: repeat(auto-fit, minmax(0, auto));
        padding: 0 $gutter;

        .assignment-dates {
          &.after-start-before-due-submitted,
          &.after-due-submitted-before-due,
          &.after-due-submitted-after-due {
            .content {
              color: var(--color-secondary);
            }
          }
        }

        .submission-date {
          &.after-due-submitted-after-due {
            .content {
              color: var(--color-danger);
            }
          }
        }

        .submission-status {
          margin: auto 0;

          .label {
            align-items: center;
            border-radius: 4px;
            color: var(--color-white);
            display: flex;
            font-size: $font-size-xs;
            font-weight: bold;
            height: 24px;
            padding: ($gutter / 4);
            width: fit-content;

            img {
              height: $icon-size-xs;
              margin: 0 4px 0 0;
            }

            &.before-start-date {
              background-color: var(--color-black-background-light);
            }

            &.after-start-before-due-not-submitted {
              background-color: var(--color-info);
            }

            &.after-start-before-due-submitted {
              background-color: var(--color-success);
            }

            &.after-due-not-submitted {
              background-color: var(--color-danger);
            }

            &.after-due-submitted-before-due {
              background-color: var(--color-success);
            }

            &.after-due-submitted-after-due {
              background-color: var(--color-success);
            }
          }
        }
      }

      .notes {
        border-top: 1px solid rgba($color-black, 0.08);
        margin: $gutter 0 0 0;
        padding: $gutter;
        word-wrap: break-word;
      }

      .title {
        color: var(--color-secondary);
        font-size: $font-size-xs;
        font-weight: bold;
      }

      .content {
        color: var(--color-dark);
        font-weight: bold;
      }
    }
  }
}

@include responsive(tablet) {
  #assignment-detail-view.layout-full-card {
    padding: $gutter $gutter 0 $gutter;

    .jz-card.content {
      margin: 0 0 $gutter 0;

      #assignment-details {
        .assignment-status {
          grid-template-rows: 100%;
          grid-template-columns: max-content max-content minmax(0, auto);
        }
      }
    }
  }
}

@include responsive(desktop) {
  #assignment-detail-view.layout-full-card {
    .jz-card.content {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;
      margin: unset;
      overflow: auto;

      #assignment-details {
        border-right: 1px solid rgba($color-black, 0.08);
        overflow: auto;
        padding-bottom: 74px;

        .assignment-status {
        }
      }

      #assignment-exercises {
        padding-bottom: 74px;
        overflow: auto;
      }
    }
  }
}
</style>
